import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.addEventListener('pointerdown', this.#handleGalleryIntent, { signal, passive: true });

    this.#hydrateSlide(this.slideshow?.current ?? 0);

    if (document.readyState === 'complete') {
      this.#queueRemainingSlidesHydration();
    } else {
      window.addEventListener('load', this.#queueRemainingSlidesHydration, { once: true, signal });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #handleSlideshowSelect = (event) => {
    const { index } = event.detail;
    this.#hydrateSlide(index);
    this.#hydrateSlide(index + 1);
  };

  #handleGalleryIntent = () => {
    const currentIndex = this.slideshow?.current ?? 0;
    this.#hydrateSlide(currentIndex);
    this.#hydrateSlide(currentIndex + 1);
    this.#queueRemainingSlidesHydration();
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #remainingSlidesHydrationQueued = false;

  #hydrateDeferredImage(image) {
    if (!(image instanceof HTMLImageElement)) return false;
    if (image.dataset.galleryDeferred !== 'true' || image.dataset.galleryLoaded === 'true') return false;

    const { galleryFetchpriority, gallerySrc, gallerySrcset } = image.dataset;

    if (galleryFetchpriority) {
      image.setAttribute('fetchpriority', galleryFetchpriority);
    }

    if (gallerySrcset) {
      image.setAttribute('srcset', gallerySrcset);
    }

    if (gallerySrc) {
      image.setAttribute('src', gallerySrc);
    }

    image.dataset.galleryLoaded = 'true';
    return true;
  }

  #hydrateDeferredImagesInContainer(container) {
    if (!(container instanceof HTMLElement)) return false;

    let hydrated = false;
    container.querySelectorAll('img[data-gallery-deferred="true"]').forEach((image) => {
      hydrated = this.#hydrateDeferredImage(image) || hydrated;
    });

    return hydrated;
  }

  #hydrateSlide(index) {
    if (typeof index !== 'number' || index < 0) return false;

    const slide = this.slideshow?.slides?.[index];
    if (slide instanceof HTMLElement) {
      return this.#hydrateDeferredImagesInContainer(slide);
    }

    const mediaItem = this.media?.[index];
    if (mediaItem instanceof HTMLElement) {
      return this.#hydrateDeferredImagesInContainer(mediaItem);
    }

    return false;
  }

  #hydrateRemainingSlides = () => {
    this.#remainingSlidesHydrationQueued = false;

    const slides = this.slideshow?.slides ?? [];
    slides.forEach((_, index) => {
      if (index === 0) return;
      this.#hydrateSlide(index);
    });
  };

  #queueRemainingSlidesHydration = () => {
    if (this.#remainingSlidesHydrationQueued) return;
    this.#remainingSlidesHydrationQueued = true;

    const run = () => this.#hydrateRemainingSlides();

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1800 });
      return;
    }

    window.setTimeout(run, 900);
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
